import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import useTrackEvent from 'src/hooks/useTrackEvent'

/**
 * Four rental product pillars — paths starting with these prefixes carry the
 * corresponding marketing-pillar tag on LANDING_VIEW.
 *
 * The `almacenamiento-a-domicilio` entry is a sub-pillar of `renta-mini-bodegas`
 * and is matched BEFORE the parent prefix so its LANDING_VIEW carries the
 * `storage-ondemand` tag instead of `storage`.
 */
const RENTAL_PILLARS: Record<string, string> = {
  '/renta-mini-bodegas/almacenamiento-a-domicilio': 'storage-ondemand',
  '/renta-mini-bodegas': 'storage',
  '/pension-estacionamiento': 'parking',
  '/renta-bodegas-comerciales': 'warehouse',
  '/renta-naves-industriales': 'industrial'
}

/**
 * Deny-list: paths where LANDING_VIEW should NOT fire because the page is
 * behind auth, part of a flow, already fires another event, or is a non-user
 * asset. Matches by first path segment (or exact path for single-segment
 * routes). Everything else is treated as public and fires LANDING_VIEW.
 */
const DENY_PREFIXES = new Set([
  'api',
  'dashboard',
  'user',
  'userConfirmation',
  'checkout',
  'checkoutDetails',
  'complete-profile',
  'business',
  'authenticate',
  'login',
  'confirm-email',
  'unsubscribe',
  'suspended',
  '404',
  '500',
  'test'
])

/**
 * Normalize a Next.js asPath to a canonical pagePath:
 * strip query/hash, drop trailing slash (except root).
 */
const canonicalPath = (asPath: string): string => {
  const noQuery = asPath.split('?')[0].split('#')[0]
  if (!noQuery || noQuery === '/') return noQuery || '/'
  return noQuery.endsWith('/') ? noQuery.slice(0, -1) : noQuery
}

/**
 * Returns the pillar tag for a path, or null when LANDING_VIEW should NOT
 * fire. All public pages are tracked — the pillar is just a label that lets
 * the marketing report group traffic by intent.
 *
 * Not tracked (returns null):
 *  - Authed / flow routes (see DENY_PREFIXES)
 *  - Short-URL listings `/c/...`              — redirects to listing (VISIT)
 *  - Listing detail `/{pillar}/{city}/{hood}/{configId}[/...]` — fires VISIT
 *  - Alojamiento listing detail `/alojamiento/{id}` — it's a listing
 *
 * Pillar tags:
 *  - `home`            — `/`
 *  - `storage`         — `/renta-mini-bodegas/*`
 *  - `storage-ondemand` — `/renta-mini-bodegas/almacenamiento-a-domicilio[/{city}]` (overrides storage)
 *  - `parking`         — `/pension-estacionamiento/*`
 *  - `warehouse`     — `/renta-bodegas-comerciales/*`
 *  - `industrial`    — `/renta-naves-industriales/*`
 *  - `flotillas-b2b` — `/pension-estacionamiento/flotillas` (overrides parking)
 *  - `logistics`     — `/soluciones-logisticas/*` (3PL — standalone, not a rental pillar)
 *  - `blog`          — any `/blog` subtree (top-level or pillar-nested)
 *  - `help`          — `/acerca-de-spotme/*`
 *  - `host`          — `/anfitrion/*`
 *  - `lodging-int`   — `/alojamientos/*` (international directory)
 *  - `directory`     — `/directory/*`
 *  - `post`          — `/post/*`
 *  - `search`        — `/search`, `/begin-search`
 *  - `info`          — `/nosotros`, `/medios`, `/contacto`, `/guia-de-tamanos`,
 *                      `/terminos-y-condiciones`, `/aviso-de-privacidad`, `/sitemap`
 *  - `other`         — any other public path
 */
const resolvePillar = (path: string): string | null => {
  if (path === '/' || path === '') return 'home'

  // Listings — already fire VISIT, don't double-count
  if (path.startsWith('/c/')) return null

  // First path segment
  const segments = path.split('/').filter(Boolean)
  if (segments.length === 0) return 'home'
  const first = segments[0]

  // Deny-list: authed / flow / api / error pages
  if (DENY_PREFIXES.has(first)) return null

  // Alojamiento listing detail — skip (it's a listing page)
  if (first === 'alojamiento') return null

  // Explicit B2B flotillas — precedence over parking
  if (path === '/pension-estacionamiento/flotillas') return 'flotillas-b2b'

  // Soluciones Logísticas (3PL) — standalone top-level page, NOT under a
  // rental-pillar prefix. The supply page (/anfitrion/soluciones-logisticas)
  // does NOT mount this tracker — it fires supplyView instead (Task 9).
  if (path.startsWith('/soluciones-logisticas')) return 'logistics'

  // Almacenamiento a Domicilio — sub-pillar of storage. Matches the parent
  // landing (`/renta-mini-bodegas/almacenamiento-a-domicilio`, 2 segments)
  // and per-city LPs (`/.../{city}`, 3 segments). 4+ segments would be a
  // listing detail and fall through to the standard storage VISIT path.
  if (
    segments[0] === 'renta-mini-bodegas' &&
    segments[1] === 'almacenamiento-a-domicilio' &&
    segments.length <= 3
  ) {
    return 'storage-ondemand'
  }

  // Rental pillars — but stop at listing-detail depth (configId present)
  const pillarKey = `/${first}`
  const pillar = RENTAL_PILLARS[pillarKey]
  if (pillar) {
    // Blog nested under a pillar: /{pillar}/blog[/...]
    if (segments[1] === 'blog') return 'blog'
    // 1..3 segments = pillar | city | hood LPs (public)
    // 4+ segments = listing detail (configId) — already fires VISIT
    if (segments.length <= 3) return pillar
    return null
  }

  // Non-rental public sections
  if (first === 'blog') return 'blog'
  if (first === 'acerca-de-spotme') return 'help'
  if (first === 'anfitrion') return 'host'
  if (first === 'alojamientos') return 'lodging-int'
  if (first === 'directory') return 'directory'
  if (first === 'post') return 'post'
  if (first === 'search' || first === 'begin-search') return 'search'
  if (
    first === 'nosotros' ||
    first === 'medios' ||
    first === 'contacto' ||
    first === 'guia-de-tamanos' ||
    first === 'terminos-y-condiciones' ||
    first === 'aviso-de-privacidad' ||
    first === 'sitemap'
  ) {
    return 'info'
  }

  // Public but unclassified — still track so coverage is complete
  return 'other'
}

/**
 * Fires LANDING_VIEW at most once per (session, pillar) on tracked LP URLs.
 * A 40-page storage-only session collapses to a single event (the entry
 * page) — the LP report counts sessions-per-entry-page, so intra-pillar
 * browsing is pure noise. Pillar crossovers still fire (storage → parking
 * is a real signal worth keeping).
 * Must be mounted INSIDE ApolloProvider — uses useMutation under the hood.
 * Mounted next to <UTMs /> in _app.tsx so it runs inside all the providers.
 */
const LandingViewTracker: React.FC = () => {
  const router = useRouter()
  const { trackEvent, EventAction } = useTrackEvent()

  useEffect(() => {
    const fire = (asPath: string) => {
      const path = canonicalPath(asPath)
      const pillar = resolvePillar(path)
      if (!pillar) return
      if (typeof window === 'undefined') return
      try {
        // Key on pillar, not path: one event per (session, pillar) preserves
        // entry-LP attribution + pillar switches while dropping every
        // same-pillar navigation that follows. Value is the first path we
        // saw for the pillar (useful when debugging from DevTools).
        const key = `lv:pillar:${pillar}`
        if (window.sessionStorage.getItem(key)) return
        window.sessionStorage.setItem(key, path)
      } catch {
        // sessionStorage unavailable (private mode / Safari quirks) — still fire once
      }
      trackEvent({
        action: EventAction.LandingView,
        pagePath: path,
        pillar
      })
    }

    // Initial landing
    fire(router.asPath)

    // SPA navigation into another LP
    const onRoute = (url: string) => fire(url)
    router.events.on('routeChangeComplete', onRoute)
    return () => {
      router.events.off('routeChangeComplete', onRoute)
    }
  }, [router, trackEvent, EventAction])

  return null
}

export default LandingViewTracker
